import type { ScheduleConflict } from './scheduleConflicts';

type NamedRecord = { id: string; name: string };

/** Older snapshots retained messages only. Resolve repair targets only by unique,
 * exact catalog/crew matches; never guess IDs or overwrite saved identifiers. */
export function resolveSavedConflicts(
  conflicts: ScheduleConflict[],
  technicians: NamedRecord[],
  serviceTypes: NamedRecord[],
): ScheduleConflict[] {
  /** The saved id if there is one, else the crew member that name names — and
   *  only when exactly one does, so a repeated name resolves to nobody. */
  const technicianFor = (conflict: ScheduleConflict, technicianName?: string) => {
    if (conflict.technicianId) return conflict.technicianId;
    const matches = technicians.filter((tech) => tech.name === technicianName);
    return matches.length === 1 ? matches[0].id : undefined;
  };

  return conflicts.map((conflict) => {
    // Out of area. Same repair as the skill case below: without a ZIP and a
    // technician to attach it to there is no "Add ZIP code" to offer, and a
    // text-only snapshot carries both only inside its message.
    if (conflict.type === 'TECH_TERRITORY_MISMATCH') {
      const match = conflict.message.match(/^(.+) does not cover ZIP (.+)\.$/);
      const technicianName =
        typeof conflict.details?.technicianName === 'string'
          ? conflict.details.technicianName
          : match?.[1];
      const zipCode =
        typeof conflict.details?.zipCode === 'string' ? conflict.details.zipCode : match?.[2];
      // "does not serve this territory" names no ZIP, and one ZIP would not
      // answer it — such a conflict is left exactly as saved.
      if (!zipCode) return conflict;
      return {
        ...conflict,
        technicianId: technicianFor(conflict, technicianName),
        details: { ...conflict.details, technicianName, zipCode },
      };
    }

    if (conflict.type !== 'TECH_SKILL_MISMATCH') return conflict;
    const match = conflict.message.match(/^(.+) is missing the required skill: (.+)\.$/);
    const technicianName =
      typeof conflict.details?.technicianName === 'string'
        ? conflict.details.technicianName
        : match?.[1];
    const serviceTypeName =
      typeof conflict.details?.serviceTypeName === 'string'
        ? conflict.details.serviceTypeName
        : match?.[2];
    const matchingSkills = serviceTypes.filter((skill) => skill.name === serviceTypeName);
    const serviceTypeId =
      conflict.details?.serviceTypeId ??
      (matchingSkills.length === 1 ? matchingSkills[0].id : undefined);
    return {
      ...conflict,
      technicianId: technicianFor(conflict, technicianName),
      details: {
        ...conflict.details,
        technicianName,
        serviceTypeId,
        serviceTypeName:
          serviceTypeName ?? serviceTypes.find((skill) => skill.id === serviceTypeId)?.name,
      },
    };
  });
}
